import React, { Component, ErrorInfo, ReactNode } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Home, AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const LuxuryErrorFallback = ({ error, onReset }: { error: Error | null; onReset: () => void }) => (
  <div className="min-h-screen flex flex-col" style={{ background: 'hsl(var(--charcoal))' }}>
    <div className="h-[2px]" style={{ background: 'linear-gradient(to right, transparent, hsl(var(--gold)), transparent)' }} />
    <div className="flex-1 flex items-center justify-center px-4">
      <div className="text-center max-w-lg">
        <img
          src="https://ibieannzbpwoamcowznu.supabase.co/storage/v1/object/public/email-assets/exora-logo-transparent.png?v=2"
          alt="Exora" className="w-16 h-16 mx-auto mb-8 opacity-40"
        />
        <p className="text-[10px] font-medium tracking-[0.3em] mb-6"
          style={{ fontFamily: "Inter, sans-serif", color: 'hsl(var(--gold))' }}>SOMETHING WENT WRONG</p>

        <h1 className="text-[100px] sm:text-[140px] font-light leading-none mb-[-30px] sm:mb-[-40px] select-none"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: 'hsl(var(--ivory) / 0.04)' }}>500</h1>

        <h2 className="text-3xl sm:text-4xl font-light mb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: 'hsl(var(--ivory))' }}>
          An unexpected error
        </h2>
        <p className="text-lg font-light italic mb-8"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: 'hsl(var(--gold))' }}>
          occurred. We're on it.
        </p>

        <p className="text-sm font-light leading-relaxed mb-10 max-w-sm mx-auto"
          style={{ fontFamily: "Inter, sans-serif", color: 'hsl(var(--ivory) / 0.5)' }}>
          Something didn't go as planned. Please try again, or return home and we'll take it from here.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button onClick={onReset}
            className="flex items-center gap-2 transition-all duration-200"
            style={{
              fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase' as const, fontWeight: 400,
              padding: '11px 28px', borderRadius: '8px',
              background: 'hsl(var(--gold) / 0.1)', border: '1px solid hsl(var(--gold) / 0.25)', color: 'hsl(var(--gold))', cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--gold) / 0.15)'; e.currentTarget.style.borderColor = 'hsl(var(--gold) / 0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'hsl(var(--gold) / 0.1)'; e.currentTarget.style.borderColor = 'hsl(var(--gold) / 0.25)'; }}
          >
            <RefreshCw className="w-3.5 h-3.5" /> Try Again
          </button>
          <button onClick={() => window.location.href = "/"}
            className="flex items-center gap-2 transition-all duration-200"
            style={{
              fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase' as const, fontWeight: 300,
              padding: '11px 28px', borderRadius: '8px',
              background: 'transparent', border: '1px solid hsl(var(--ivory) / 0.08)', color: 'hsl(var(--ivory) / 0.4)', cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'hsl(var(--ivory) / 0.15)'; e.currentTarget.style.color = 'hsl(var(--ivory) / 0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'hsl(var(--ivory) / 0.08)'; e.currentTarget.style.color = 'hsl(var(--ivory) / 0.4)'; }}
          >
            <Home className="w-3.5 h-3.5" /> Home
          </button>
        </div>

        <div className="mt-12">
          <p className="text-[9px] font-medium tracking-[0.25em]"
            style={{ fontFamily: "Inter, sans-serif", color: 'hsl(var(--gold) / 0.4)' }}>
            EXORA CLUB
          </p>
        </div>
      </div>
    </div>
  </div>
);

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("❌ Error Boundary caught an error:", error, errorInfo);
    this.setState({ error, errorInfo });
    if (this.props.onError) this.props.onError(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <LuxuryErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
